<script>
import useValidation from "@/use/Validation";
import useSharedData from "@/use/SharedData";
import useSharedMethod from "@/use/SharedMethod";
import useTools from "@/use/Tools";
import {useMedicalCenterStore} from "@/stores/Medical/MedicalCenter";
import {computed, onMounted, ref} from "vue";
import axios from "axios";
import useSharedSelectData from "@/use/SharedSelectData";

export default {
    name: "MedicalCenterForm",
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        viewAction: Boolean,
        deleteAction: Boolean,
    },
    setup(props) {
        const readonly = computed(() => props.viewAction || props.deleteAction);
        const {mxRules} = useValidation();
        const {dialog, loading, overlay} = useSharedData();
        const {periodicity} = useSharedSelectData();
        const {getBack} = useSharedMethod();
        const {$refs, $route} = useTools();

        const medicalCenterStore = useMedicalCenterStore();

        const medicalCenter = ref({enablePeriodicity: false});

        function findById(id) {
            loading.value = true;
            axios.get(`/medicalCenters/${id}`).then((response) => {
                medicalCenter.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }


        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/medicalCenters', medicalCenter.value).then((response) => {
                    medicalCenterStore.addMedicalCenter(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/medicalCenters', medicalCenter.value).then((response) => {
                    medicalCenterStore.updateMedicalCenter(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.delete(`/medicalCenters/${medicalCenter.value.id}`).then(() => {
                    medicalCenterStore.deleteMedicalCenter(medicalCenter.value);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        });

        return {
            medicalCenterStore,
            mxRules,
            readonly,
            dialog,
            loading,
            overlay,
            periodicity,
            medicalCenter,
            save,
            update,
            remove,
            getBack,
        };
    }
}
</script>

<template>
    <v-dialog
        v-model="dialog"
        max-width="800"
        persistent
        scrollable
    >
        <v-card>
            <w-dialog-toolbar
                :view-action="viewAction"
                :delete-action="deleteAction"
                :add-action="addAction"
                :update-action="updateAction"
            />
            <w-overlay
                :overlay="overlay"
                :loading="loading"
                :delete-action="deleteAction"
                @remove="remove"
                @cancel="overlay = false"
            />
            <v-card-text>
                <v-container grid-list-md>
                    <v-form ref="form">
                        <w-fieldset :title="$t('common.details')">
                            <v-row dense>
                                <v-col cols="6">
                                    <w-label :label="$t('common.code')" required/>
                                    <w-text-field
                                        v-model="medicalCenter.code"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('common.label')" required/>
                                    <w-text-field
                                        v-model="medicalCenter.name"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('common.phone')"/>
                                    <w-text-field
                                        v-model="medicalCenter.phone"
                                        :readonly="readonly"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('common.fax')"/>
                                    <w-text-field
                                        v-model="medicalCenter.fax"
                                        :readonly="readonly"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('common.email')"/>
                                    <w-text-field
                                        v-model="medicalCenter.email"
                                        :readonly="readonly"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('common.web_site')"/>
                                    <w-text-field
                                        v-model="medicalCenter.webSite"
                                        :readonly="readonly"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('common.comment')"/>
                                    <w-simple-text-area
                                        v-model="medicalCenter.observation"
                                        :readonly="readonly"
                                    />
                                </v-col>

                            </v-row>
                        </w-fieldset>
                    </v-form>
                </v-container>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save(false)"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>